import { useEffect } from "react";
import { SITE } from "@/lib/siteConfig";

/**
 * Injects JSON-LD schema blocks for Organization, Person and the 3 Services
 * into the document head so search engines surface rich results.
 */
const SCHEMAS = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "NextChapter4You",
    url: "https://nextchapter4you.nl",
    logo: "https://nextchapter4you.nl/logo.svg",
    image: "https://nextchapter4you.nl/logo.svg",
    description:
      "Executive coaching and expat career support in the Netherlands. Human-first coaching for leaders and professionals ready to grow.",
    email: SITE.email,
    telephone: SITE.phoneRaw,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Eindhoven",
      addressCountry: "NL",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: SITE.email,
        telephone: SITE.phoneRaw,
        areaServed: "EU",
        availableLanguage: ["English", "Dutch"],
      },
    ],
    sameAs: [SITE.linkedinBrand],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "NextChapter4You",
    url: "https://nextchapter4you.nl",
    inLanguage: "en-GB",
    publisher: { "@type": "Organization", name: "NextChapter4You" },
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    name: SITE.founder,
    jobTitle: SITE.founderTitle,
    worksFor: { "@type": "Organization", name: "NextChapter4You" },
    image: SITE.founderImage,
    sameAs: [SITE.linkedinFounder],
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Professional Coaching",
    provider: { "@type": "Organization", name: "NextChapter4You" },
    areaServed: "Netherlands",
    description:
      "Executive and leadership coaching for senior professionals seeking growth, clarity and confidence.",
    url: "https://nextchapter4you.nl/professional-coaching",
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Expat Outplacement Support",
    provider: { "@type": "Organization", name: "NextChapter4You" },
    areaServed: "Netherlands",
    description:
      "Career transition support for expat professionals in the Dutch job market: CV, LinkedIn, interview coaching and resilience.",
    url: "https://nextchapter4you.nl/expat-outplacement-support",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How many coaching sessions do I need?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We typically recommend a minimum of 6 sessions for meaningful progress, with many clients continuing for 3 to 6 months.",
        },
      },
      {
        "@type": "Question",
        name: "Is coaching available online?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. All coaching is available online via video call, in-person in the Netherlands, or a combination of both.",
        },
      },
      {
        "@type": "Question",
        name: "How is coaching different from therapy or consulting?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Coaching focuses on forward momentum: your goals, your growth, your future. We don't diagnose or treat; we challenge, support and guide.",
        },
      },
      {
        "@type": "Question",
        name: "Who delivers the coaching?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Coaching is led by Ashutosh Pandey, with deep experience in both executive leadership and personal development across international environments.",
        },
      },
    ],
  },
];

export default function SchemaJSONLD() {
  useEffect(() => {
    const id = "nc4y-jsonld";
    document.querySelectorAll(`script[data-nc4y-jsonld]`).forEach((n) => n.remove());
    SCHEMAS.forEach((schema, i) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.nc4yJsonld = String(i);
      s.id = `${id}-${i}`;
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
    });
  }, []);
  return null;
}
